// @flow

import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { useUserInputAsTextField } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';

import { TextField } from '../../../atoms';
import { ErrorLabel } from '../../../molecules/error-label';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import type { Country } from '@tectonics/prs/country';
import { getCountry, useIncomingPayment } from '@tectonics/prs/incoming-payment';
import { translate } from '@tectonics/translate';

export function FieldCustomerEmail(): Node {
  const inputId = useUniqueId();
  const [value, handleChange] = useUserInputAsTextField('customerEmail');
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('customerEmail');
  const submitting = useSubmitting();
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  return (
    <div data-test-name="customer-email">
      <label htmlFor={inputId}>
        <LabelMessage />
      </label>

      <TextField
        id={inputId}
        type="email"
        value={value}
        onChange={handleChange}
        fashion={formHasBeenSent && validationError ? 'error' : null}
        readOnly={submitting}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        name="customer-email"
      />

      {formHasBeenSent && validationError && (
        <ValidationMessage country={country} validationError={validationError} />
      )}
    </div>
  );
}

function LabelMessage(): string {
  return translate('volcanes.inputCustomerEmail');
}

type ValidationErrorProps = {|
  country: Country,
  validationError: UserInputValidationError,
|};

const messages = (validationError: UserInputValidationError) => {
  switch (validationError.id) {
    case 'empty':
      return {
        br: 'Por favor, informe seu e-mail',
        mx: '¡Ey!, has olvidado de informar tu Correo Electronico.',
        ar: '¡Ey!, has olvidado de informar tu Correo Electronico.',
        co: '¡Ey!, has olvidado de informar tu Correo Electronico.',
        cl: '¡Ey!, has olvidado de informar tu Correo Electronico.',
        pe: '¡Ey!, has olvidado de informar tu Correo Electronico.',
        ec: '¡Ey!, has olvidado de informar tu Correo Electronico.',
        bo: '¡Ey!, has olvidado de informar tu Correo Electronico.',
        uy: '¡Ey!, has olvidado de informar tu Correo Electronico.',
        py: '¡Ey!, has olvidado de informar tu Correo Electronico.',
        cr: '¡Ey!, has olvidado de informar tu Correo Electronico.',
        do: '¡Ey!, has olvidado de informar tu Correo Electronico.',
        pa: '¡Ey!, has olvidado de informar tu Correo Electronico.',
        sv: '¡Ey!, has olvidado de informar tu Correo Electronico.',
        gt: '¡Ey!, has olvidado de informar tu Correo Electronico.',
        za: 'Please enter your e-mail address',
        ke: 'Please enter your e-mail address',
        ng: 'Please enter your e-mail address',
        in: 'Please enter your e-mail address',
        tz: 'Please enter your e-mail address',
        ug: 'Please enter your e-mail address',
        zm: 'Please enter your e-mail address',
        eg: 'يرجى إضافة "@" في عنوان البريد الإلكتروني. "useremail.com" ينقصه "@".',
        gh: 'Please enter your e-mail address',
        jm: 'Please enter your e-mail address',
      };

    default:
      return {
        br: 'Endereço de e-mail inválido',
        mx: 'Email inválido',
        ar: 'Email inválido',
        co: 'Email inválido',
        cl: 'Email inválido',
        pe: 'Email inválido',
        ec: 'Email inválido',
        bo: 'Email inválido',
        uy: 'Email inválido',
        py: 'Email inválido',
        cr: 'Email inválido',
        sv: 'Email inválido',
        gt: 'Email inválido',
        do: 'Email inválido',
        pa: 'Email inválido',
        za: 'Invalid email',
        ke: 'Invalid email',
        ng: 'Invalid email',
        in: 'Invalid email',
        tz: 'Invalid email',
        ug: 'Invalid email',
        zm: 'Invalid email',
        eg: 'يرجى إضافة "@" في عنوان البريد الإلكتروني. "useremail.com" ينقصه "@".',
        gh: 'Invalid email',
        jm: 'Invalid email',
      };
  }
};

function ValidationMessage({ country, validationError }: ValidationErrorProps): Node {
  return (
    <ErrorLabel>
      {messages(validationError)[country] || (serverErrorMessage(validationError.payload))}
    </ErrorLabel>
  );
}
